import { css } from '@emotion/css';
import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, XIcon } from '@persona-id/design-tokens/icons';
import { flatMap, isEqual, last } from 'lodash/fp';
import {
  DetailedHTMLProps,
  DetailsHTMLAttributes,
  ReactNode,
  RefCallback,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// eslint-disable-next-line no-restricted-imports
import Select, {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  InputProps,
  MenuListProps,
  MenuPlacement,
  MenuProps,
  MultiValue,
  MultiValueProps,
  OptionProps,
  Props,
  PropsValue,
  SelectInstance,
  SingleValue,
  SingleValueProps,
  StylesConfig,
} from 'react-select';
import CreatableSelect, { CreatableProps } from 'react-select/creatable';
import styled, { DefaultTheme, ThemeContext } from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import Sticker from 'design-system/components/Sticker';
import { Elevation } from 'design-system/variables/effect';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import usePrevious from 'lib/hooks/usePrevious';

/**
 * https://github.com/JedWatson/react-select/issues/4804#issuecomment-927223471
 */
declare module 'react-select/dist/declarations/src/Select' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface Props<Option, IsMulti, Group extends GroupBase<Option>> {
    onFocusOption?: (value: string) => void;
    isSelectedOptionRequired?: boolean;
  }
}

type OptionColor = 'purple' | 'red' | 'yellow' | 'green' | 'blue' | 'teal' | 'slate';

export interface OptionType<ValueType = string> {
  label: ReactNode;
  value: ValueType;
  disabled?: boolean;
  options?: OptionType<ValueType>[];
  onSelect?: () => void;
  color?: OptionColor;
}

interface CustomReactSelectProps<T> {
  hasError?: boolean;
  value: T | T[];
  portal?: boolean;
  preserveOrder?: boolean;
  onChange?: (value: any) => void;
  onInputChange?: (value: any, actionMeta: any) => void;
  onFocusOption?: (value: string) => void;
  size?: 'md' | 'sm';
  disabled?: boolean;
  inputRef?: RefCallback<SelectInstance>;
  preserveOptionOnChange?: boolean;
  /**
   * Grow vertically as options are selected.
   */
  autoExpand?: boolean;
  /**
   * Generally speaking, sort should be false if the option values are a
   * constant. We assume the constant values are defined in the order to be
   * presented.
   */
  sort?: boolean;
  // All other props passed to the select for custom components
  [x: string]: any;
}

type ReactSelectProps<
  ValueType = string,
  Option extends OptionType<ValueType> = OptionType<ValueType>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<Props<Option, IsMulti, Group>, 'onChange' | 'value'> & CustomReactSelectProps<ValueType>;

type ReactCreatableSelectProps<
  ValueType = string,
  Option extends OptionType<ValueType> = OptionType<ValueType>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<CreatableProps<Option, IsMulti, Group>, 'onChange' | 'value'> &
  CustomReactSelectProps<ValueType>;

const DropdownIndicatorContainer = styled.div<{
  disabled: boolean;
}>`
  display: inline-flex;
  color: ${({ theme, disabled }) =>
    disabled ? theme.color.text.disabled : theme.color.primary.black};
  pointer-events: none;
  vertical-align: middle;
`;

// We pass {} to customStyles because we don't want original styles over-
// writing our component styles.
export const defaultReactSelectStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  customStyles,
  hasError,
  portal,
  theme,
  autoExpand,
}: {
  customStyles: StylesConfig<Option, IsMulti, Group>;
  hasError?: boolean;
  portal?: boolean;
  theme: DefaultTheme;
  autoExpand?: boolean;
}): StylesConfig<Option, IsMulti, Group> => ({
  ...customStyles,
  container: (styles, state) => ({
    ...styles,
    width: '100%',

    ...(customStyles.container != null && customStyles.container({}, state)),
  }),
  control: (styles, state) => ({
    ...styles,
    borderRadius: parseInt(theme.element.borderRadius, 10),
    borderColor:
      state.menuIsOpen || state.isFocused ? theme.color.border.active : theme.color.border.input,
    width: '100%',
    minHeight: '32px',
    boxShadow: state.isFocused ? `0 0 0 1px ${theme.color.border.active}` : 'none',
    '&:hover': {
      boxShadow:
        state.menuIsOpen || state.isFocused
          ? `0 0 0 1px ${theme.color.border.active}, ${Elevation.Mid}`
          : `0 0 0 0, ${Elevation.Mid}`,
    },
    transition: TransitionDuration.Fast,
    // TODO when there's a way to enable scroll and disable remove together
    ...(hasError
      ? {
          borderColor: theme.color.variants.danger.default,
          color: theme.color.variants.danger.default,
          boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}`,
          '&:hover': {
            boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}, ${Elevation.Mid}`,
          },
        }
      : undefined),
    ...(customStyles.control != null && customStyles.control({}, state)),
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: undefined,
    padding: undefined,
    ...(customStyles.dropdownIndicator != null && customStyles.dropdownIndicator({}, state)),
  }),
  indicatorsContainer: (styles, state) => ({
    ...styles,
    height: '30px',
    marginRight: Spacing[2],
    ...(customStyles.indicatorsContainer != null && customStyles.indicatorsContainer({}, state)),
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    display: 'none',
    ...(customStyles.indicatorSeparator != null && customStyles.indicatorSeparator({}, state)),
  }),
  menu: (styles, state) => ({
    ...styles,
    boxShadow: Elevation.High,
    ...(customStyles.menu != null && customStyles.menu({}, state)),
  }),
  menuList: (styles, state) => ({
    ...styles,
    padding: BASE,
    ...(customStyles.menuList != null && customStyles.menuList({}, state)),
  }),
  menuPortal: (styles, state) => ({
    ...styles,
    ...(portal ? { zIndex: Z_INDICES.modalDropdown } : {}),
    ...(customStyles.menuPortal != null && customStyles.menuPortal({}, state)),
  }),
  option: (styles, state) => {
    const baseStyles = {
      ...styles,
      backgroundColor: 'transparent',
      borderRadius: theme.element.borderRadius,
      display: 'flex',
      alignItems: 'center',
      padding: `${Spacing[3]} ${Spacing[2]}`,
      fontWeight: theme.text.paragraph.lg.fontWeight,
      transitionDuration: TransitionDuration.Fast,
      ':active': {
        // Slightly higher than focused style for mobile
        // https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/components/Option.js#L66
        backgroundColor: theme.color.background.highlight,
        color: theme.color.primary.brand,
      },
    };
    const focusedStyles = state.isFocused
      ? {
          backgroundColor: theme.color.background.highlight,
          ':active': {
            backgroundColor: theme.color.background.highlight,
          },
        }
      : {};
    const selectedStyles = state.isSelected
      ? {
          backgroundColor: theme.color.background.highlight,
          color: theme.color.primary.brand,
          ':active': {
            backgroundColor: theme.color.background.highlight,
          },
        }
      : {};
    const disabledStyles = state.isDisabled
      ? {
          backgroundColor: 'transparent',
          color: theme.color.text.disabled,
          ':active': {
            backgroundColor: 'transparent',
          },
          overflow: 'auto',
        }
      : {};

    return {
      ...baseStyles,
      ...focusedStyles,
      ...selectedStyles,
      ...disabledStyles,
      ...(customStyles.option != null && customStyles.option({}, state)),
    };
  },
  placeholder: (styles, state) => ({
    ...styles,
    ...(hasError
      ? {
          color: theme.color.variants.danger.default,
        }
      : undefined),
    ...(customStyles.placeholder != null && customStyles.placeholder({}, state)),
  }),
  valueContainer: (styles, state) => ({
    ...styles,
    fontSize: parseInt(theme.text.paragraph.md.fontSize, 10),
    fontWeight: theme.text.paragraph.md.fontWeight,
    fontFamily: theme.text.fontFamily.base,
    textDecoration: 'none',
    height: autoExpand ? 'auto' : '30px', // Account for border being set on the container
    overflow: 'auto',
    outline: '0 !important',
    padding: autoExpand ? `${Spacing[1]} ${Spacing[2]}` : `0px ${Spacing[2]}`,
    ...(state.isMulti && {
      gap: `${Spacing[1]}`,
      lineHeight: 'normal',
    }),
    ...(customStyles.valueContainer != null && customStyles.valueContainer({}, state)),
  }),
  input: (styles, state) => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
    margin: 0,
    ...(state.isMulti && {
      margin: 0,
    }),
    ...(state.isDisabled && { visibility: 'visible' }),
    ...(customStyles.input != null && customStyles.input({}, state)),
  }),
  singleValue: (styles, state) => ({
    ...styles,
    ...(customStyles.singleValue != null && customStyles.singleValue({}, state)),
  }),
});

const defaultSmStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  customStyles: StylesConfig<Option, IsMulti, Group>,
  hasError: boolean | undefined,
  portal: boolean | undefined,
  theme: DefaultTheme,
  autoExpand: boolean,
): StylesConfig<Option, IsMulti, Group> => ({
  ...customStyles,
  container: (styles, state) => ({
    ...styles,
    width: '100%',
    ...(customStyles.container != null && customStyles.container({}, state)),
  }),
  control: (styles, state) => ({
    ...styles,
    borderRadius: parseInt(theme.element.borderRadius, 10),
    borderColor:
      state.menuIsOpen || state.isFocused ? theme.color.border.active : theme.color.border.input,
    width: '100%',
    minHeight: 'auto',
    boxShadow: state.isFocused ? `0 0 0 1px ${theme.color.border.active}` : 'none',
    '&:hover': {
      boxShadow:
        state.menuIsOpen || state.isFocused
          ? `0 0 0 1px ${theme.color.border.active}, ${Elevation.Mid}`
          : `0 0 0 0, ${Elevation.Mid}`,
    },
    transition: TransitionDuration.Fast,
    ...(hasError
      ? {
          borderColor: theme.color.variants.danger.default,
          color: theme.color.variants.danger.default,
          boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}`,
          '&:hover': {
            boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}, ${Elevation.Mid}`,
          },
        }
      : undefined),
    ...(customStyles.control != null && customStyles.control({}, state)),
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: undefined,
    padding: undefined,
    ...(customStyles.dropdownIndicator != null && customStyles.dropdownIndicator({}, state)),
  }),
  singleValue: (styles, state) => ({
    ...styles,
    fontSize: theme.text.paragraph.sm.fontSize,
    fontWeight: theme.text.paragraph.sm.fontWeight,
    lineHeight: theme.text.paragraph.sm.lineHeight,
    ...(customStyles.singleValue != null && customStyles.singleValue({}, state)),
  }),
  indicatorsContainer: (styles, state) => ({
    ...styles,
    height: '22px',
    paddingRight: Spacing[2],
    ...(customStyles.indicatorsContainer != null && customStyles.indicatorsContainer({}, state)),
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    display: 'none',
    ...(customStyles.indicatorSeparator != null && customStyles.indicatorSeparator({}, state)),
  }),
  menu: (styles, state) => ({
    ...styles,
    boxShadow: Elevation.High,
    ...(customStyles.menu != null && customStyles.menu({}, state)),
  }),
  menuList: (styles, state) => ({
    ...styles,
    padding: BASE,
    ...(customStyles.menuList != null && customStyles.menuList({}, state)),
  }),
  menuPortal: (styles, state) => ({
    ...styles,
    ...(portal ? { zIndex: Z_INDICES.modalDropdown } : {}),
    ...(customStyles.menuPortal != null && customStyles.menuPortal({}, state)),
  }),
  option: (styles, state) => {
    const baseStyles = {
      ...styles,
      backgroundColor: 'transparent',
      borderRadius: theme.element.borderRadius,
      display: 'flex',
      alignItems: 'center',
      padding: `${Spacing[2]} ${Spacing[2]}`,
      transitionDuration: TransitionDuration.Fast,
      fontSize: theme.text.paragraph.md.fontSize,
      fontWeight: theme.text.paragraph.md.fontWeight,
      lineHeight: theme.text.paragraph.md.lineHeight,
      ':active': {
        // Slightly higher than focused style for mobile
        // https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/components/Option.js#L66
        backgroundColor: theme.color.background.highlight,
        color: theme.color.primary.brand,
      },
    };
    const focusedStyles = state.isFocused
      ? {
          backgroundColor: theme.color.background.highlight,
          ':active': {
            backgroundColor: theme.color.background.highlight,
          },
        }
      : {};
    const selectedStyles = state.isSelected
      ? {
          backgroundColor: theme.color.background.highlight,
          color: theme.color.primary.brand,
          ':active': {
            backgroundColor: theme.color.background.highlight,
          },
        }
      : {};
    const disabledStyles = state.isDisabled
      ? {
          backgroundColor: 'transparent',
          color: theme.color.text.disabled,
          ':active': {
            backgroundColor: 'transparent',
          },
        }
      : {};

    return {
      ...baseStyles,
      ...focusedStyles,
      ...selectedStyles,
      ...disabledStyles,
      ...(customStyles.option != null && customStyles.option({}, state)),
    };
  },
  placeholder: (styles, state) => ({
    ...styles,
    ...(hasError
      ? {
          color: theme.color.variants.danger.default,
        }
      : undefined),
    ...(customStyles.placeholder != null && customStyles.placeholder({}, state)),
  }),
  valueContainer: (styles, state) => ({
    ...styles,
    fontFamily: theme.text.fontFamily.base,
    textDecoration: 'none',
    outline: '0 !important',
    height: autoExpand ? 'auto' : '22px', // Account for border being set on the container
    overflow: 'auto',
    fontSize: theme.text.paragraph.sm.fontSize,
    fontWeight: theme.text.paragraph.sm.fontWeight,
    lineHeight: theme.text.paragraph.sm.lineHeight,
    padding: autoExpand ? `${Spacing[1]} ${Spacing[2]}` : `0px ${Spacing[2]}`,
    ...(state.isMulti && {
      gap: `${Spacing[2]} ${Spacing[1]}`,
      lineHeight: 'normal',
    }),
    ...(customStyles.valueContainer != null && customStyles.valueContainer({}, state)),
  }),
  input: (styles, state) => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
    ...(state.isMulti && {
      margin: 0,
    }),
    ...(state.isDisabled && { visibility: 'visible' }),
    ...(customStyles.input != null && customStyles.input({}, state)),
  }),
});

export const useCustomReactSelectComponents = <
  ValueType = string,
  Option extends OptionType<ValueType> = OptionType<ValueType>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  propComponents: ReactSelectProps<ValueType, Option, IsMulti, Group>['components'],
  options: {
    hasError?: boolean;
    setMenuPlacement?: (placement: MenuPlacement) => void;
  } & Pick<CustomReactSelectProps<ValueType>, 'size'> = {},
) =>
  useMemo(
    () => ({
      // A lot of convoluted work to add a stable id to each option
      Option: (props: OptionProps<Option, IsMulti, Group>) => {
        const theme = useContext(ThemeContext);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const index = last(props.innerProps.id.split('-'));
        const id =
          props.selectProps.id != null ? `${props.selectProps.id}-option-${index}` : undefined;
        const onFocus = () => {
          props.selectProps.onFocusOption &&
            props.selectProps.onFocusOption(props.data.value as string);
        };

        // For keyboard control, dispatch the onFocusOption event when unfocused -> focused only
        const previous = usePrevious(props.isFocused);
        useEffect(() => {
          if (previous === false && props.isFocused) onFocus();
        }, [props.isFocused, onFocus]);

        if (props.data.color != null) {
          return (
            <div onMouseOver={onFocus}>
              <components.Option {...props} innerProps={{ ...props.innerProps, id }}>
                <ColoredSingleValue theme={theme} color={props.data.color}>
                  {props.children}
                </ColoredSingleValue>
              </components.Option>
            </div>
          );
        } else {
          return (
            <div onMouseOver={onFocus}>
              <components.Option {...props} innerProps={{ ...props.innerProps, id }}>
                {props.children}
              </components.Option>
            </div>
          );
        }
      },
      SingleValue: (props: SingleValueProps<Option, IsMulti, Group>) => {
        const theme = useContext(ThemeContext);

        if (props.data.color != null) {
          return <ColoredSingleValue {...props} theme={theme} color={props.data.color} />;
        } else {
          return <components.SingleValue {...props} />;
        }
      },
      DropdownIndicator: (props: DropdownIndicatorProps<Option, IsMulti, Group>) => {
        const { isMulti, value, isDisabled } = props.selectProps;
        const theme = useContext(ThemeContext);

        // We only hide the dropdown indicator for multiselect. Always show for single.
        const hidden = isMulti && Array.isArray(value) && value.length > 0;

        // overwrite React-Select props.theme
        return hidden ? null : (
          <DropdownIndicatorContainer
            disabled={isDisabled}
            theme={theme}
            className={props.cx({}, css(props.getStyles('dropdownIndicator', props)))}
          >
            <CaretDownIcon />
          </DropdownIndicatorContainer>
        );
      },
      MultiValue: (props: MultiValueProps<Option, IsMulti, Group>) => {
        const { onClick: onDismiss, ...removeProps } = props.removeProps;
        return (
          <Sticker
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onDismiss={props.isDisabled ? null : onDismiss}
            dismissButtonProps={{
              'aria-label': `Remove ${props.children || 'option'}`,
              type: 'button',
              // react-select is a div while our Sticker "x" element is an actual button
              ...(removeProps as DetailedHTMLProps<
                DetailsHTMLAttributes<HTMLElement>,
                HTMLElement
              >),
            }}
            variant='primary'
            disabled={props.isDisabled}
          >
            {props.children}
          </Sticker>
        );
      },
      Input: (props: InputProps<Option, IsMulti, Group>) => {
        // aria-invalid of <Select> isn't supported in react-select v3...
        let id = props.id;
        let listboxId = props['aria-owns'];

        if (props.selectProps.id) {
          if (!props.selectProps.inputId) {
            id = `${props.selectProps.id}-input`;
          }
          listboxId = `${props.selectProps.id}-listbox`;
        }

        const ariaControls = listboxId;
        const ariaOwns = listboxId;

        return (
          <components.Input
            {...props}
            aria-invalid={options.hasError}
            id={id}
            aria-controls={ariaControls}
            aria-owns={ariaOwns}
          />
        );
      },
      ClearIndicator: (props: ClearIndicatorProps<Option, IsMulti, Group>) => {
        const {
          getStyles,
          innerProps: { ref, ...restInnerProps },
          cx,
        } = props;

        return (
          <div
            {...restInnerProps}
            ref={ref}
            className={cx({}, css(getStyles('clearIndicator', props)))}
            aria-label='Clear current selection'
          >
            <XIcon />
          </div>
        );
      },
      Menu: (props: MenuProps<Option, IsMulti, Group>) => {
        let id = props.innerProps.id;

        // Disabled on isSearchable because we can't modify the props of DummyInput
        if (props.selectProps.isSearchable && props.selectProps.id) {
          id = `${props.selectProps.id}-listbox`;
        }

        return <components.Menu {...props} innerProps={{ ...props.innerProps, id }} />;
      },
      MenuList: (props: MenuListProps<Option, IsMulti, Group>) => {
        const menuListRef = useRef<HTMLDivElement>();
        useLayoutEffect(() => {
          if (!options.setMenuPlacement) {
            return;
          }
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const { y, height } = menuListRef.current.getBoundingClientRect();
          if (y + height > window.innerHeight) {
            options.setMenuPlacement('top');
          }
        }, [props.options.length]);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return <components.MenuList {...props} innerRef={menuListRef} />;
      },
      ...propComponents,
    }),
    [],
  );

const sortOptions = <
  ValueType = string,
  Option extends OptionType<ValueType> = OptionType<ValueType>,
>(
  options: Option[],
): Option[] =>
  options
    .map((option) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'options' in option ? { ...option, options: sortOptions(option.options) } : option,
    )
    .sort((a, b) => {
      if (typeof a.label === 'string' && typeof b.label === 'string') {
        return a.label.localeCompare(b.label);
      } else {
        // Cannot sort React nodes
        return 0;
      }
    });

/**
 * A rich dropdown select that supports typeahead, multi-select, and option
 * creation. For simpler use cases, use `<Select/>`.
 */
const ReactSelect = <
  ValueType = string,
  Option extends OptionType<ValueType> = OptionType<ValueType>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  className,
  options: rawOptions,
  value,
  isMulti,
  isSearchable,
  placeholder,
  preserveOrder,
  onChange,
  onInputChange,
  styles: customStyles = {},
  menuPortalTarget = document.body,
  isOptionDisabled = (opt: Option) => !!opt.disabled,
  hasError,
  components: propComponents,
  portal,
  onFocusOption,
  size = 'md',
  autoExpand = true,
  inputRef,
  sort = false,
  ...passthroughProps
}: ReactSelectProps<ValueType, Option, IsMulti, Group>) => {
  const theme = useContext(ThemeContext);

  const options = useMemo<Option[]>(() => {
    if (rawOptions == null) {
      return [];
    }
    return sort ? sortOptions<ValueType, Option>(rawOptions as Option[]) : (rawOptions as Option[]);
  }, [sort, rawOptions]);

  const [menuPlacement, setMenuPlacement] = useState<MenuPlacement>('auto');
  const handleChange = useCallback(
    (selected: Option | Option[]) => {
      if (onChange == null) {
        return;
      }
      if (isMulti) {
        return onChange(selected ? (selected as Option[]).map((v) => v.value) : []);
      } else {
        // allows for options to specify side effects on select, so they can act as buttons
        const typedSelected = selected as Option;
        if (typedSelected?.onSelect instanceof Function) {
          typedSelected.onSelect();
        }
        return onChange(selected ? typedSelected.value : ('' as ValueType));
      }
    },
    [onChange, isMulti],
  );

  const components = useCustomReactSelectComponents<ValueType, Option, IsMulti, Group>(
    propComponents,
    {
      hasError,
      size,
      setMenuPlacement,
    },
  );

  const transformedValue = isMulti
    ? transformToReactSelectValue<ValueType, Option>({
        inputValue: value as MultiValue<Option>,
        options: options as Option[],
        preserveOrder: preserveOrder || false,
        isMulti: true,
      })
    : transformToReactSelectValue<ValueType, Option>({
        inputValue: value as SingleValue<Option>,
        options: options as Option[],
        preserveOrder: false,
        isMulti: false,
      });

  const styles = (() => {
    switch (size) {
      case 'md':
        return defaultReactSelectStyles({ customStyles, hasError, portal, theme, autoExpand });
      case 'sm':
        return defaultSmStyles(customStyles, hasError, portal, theme, autoExpand);
    }
  })();

  return (
    <Select<Option, IsMulti, Group>
      // render dropdown in a portal for proper behavior in modals, etc.
      menuPortalTarget={portal ? menuPortalTarget : undefined}
      menuPlacement={menuPlacement}
      {...passthroughProps}
      // support both Persona convention and original prop
      isDisabled={passthroughProps.disabled || passthroughProps.isDisabled}
      isOptionDisabled={isOptionDisabled}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange={handleChange}
      onInputChange={onInputChange}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={transformedValue}
      options={options}
      isMulti={isMulti}
      isSearchable={isSearchable}
      placeholder={placeholder}
      className={className}
      styles={styles}
      components={components}
      onFocusOption={onFocusOption}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ref={inputRef}
    />
  );
};

export default ReactSelect;

/**
 * Handles conversion between Redux form and React select value formats:
 *
 * Singular value:
 *   Redux form: 'grape'
 *   React select: { value: 'grape', label: 'Grape' }
 *
 * Multi value:
 *   Redux form: ['grape']
 *   React select: [{ value: 'grape', label: 'Grape' }]
 */
function transformToReactSelectValue<
  ValueType = string,
  Option extends OptionType<ValueType> = OptionType<ValueType>,
>({
  inputValue,
  options,
  preserveOrder,
  isMulti,
}: {
  inputValue: PropsValue<Option>;
  options: Option[];
  preserveOrder: boolean;
  isMulti: boolean;
}) {
  const ungroupedOptions = flatMap(
    (option) => ('options' in option ? option.options : option),
    options,
  );

  if (isMulti) {
    if (typeof inputValue === 'string' || inputValue == null) {
      return [];
    } else if (preserveOrder) {
      return (inputValue as MultiValue<Option>)
        .map((v) => ungroupedOptions.find((option) => isEqual(v, option?.value)))
        .filter(Boolean);
    } else {
      return ungroupedOptions.filter((option) =>
        (inputValue as MultiValue<Option>).some((v) => isEqual(v, option?.value)),
      );
    }
  }

  // Passing `undefined` will not clear an existing value. To clear a value,
  // `null` needs to be passed instead.
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return ungroupedOptions.find((option) => option.value === inputValue) ?? null;
}

export const ReactCreatableSelect = <
  ValueType = string,
  Option extends OptionType<ValueType> = OptionType<ValueType>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  className,
  options,
  value,
  isMulti,
  isSearchable,
  placeholder,
  preserveOrder,
  onChange,
  styles: customStyles = {},
  menuPortalTarget = document.body,
  isOptionDisabled = (opt: Option) => !!opt.disabled,
  hasError,
  components: propComponents,
  portal,
  onFocusOption,
  size = 'md',
  inputRef,
  preserveOptionOnChange,
  autoExpand = true,
  ...passthroughProps
}: ReactCreatableSelectProps<ValueType, Option, IsMulti, Group>) => {
  const theme = useContext(ThemeContext);
  const handleChange = useCallback(
    (selected: Option | Option[]) => {
      if (isMulti) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return onChange(selected ? (selected as Option[]).map((v) => v.value?.trim()) : []);
      } else if (preserveOptionOnChange) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return onChange(selected ? (selected as Option) : null);
      } else {
        // allows for options to specify side effects on select, so they can act as buttons
        if ((selected as Option)?.onSelect instanceof Function) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          (selected as Option).onSelect();
        }
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return onChange(selected ? (selected as Option).value?.trim() : '');
      }
    },
    [onChange, isMulti],
  );

  const components = useCustomReactSelectComponents<ValueType, Option, IsMulti, Group>(
    propComponents,
    { hasError },
  );

  const transformedValue = isMulti
    ? transformToReactSelectValue<ValueType, Option>({
        inputValue: value as MultiValue<Option>,
        options: options as Option[],
        preserveOrder: preserveOrder || false,
        isMulti: true,
      })
    : preserveOptionOnChange
      ? value
      : transformToReactSelectValue<ValueType, Option>({
          inputValue: value as SingleValue<Option>,
          options: options as Option[],
          preserveOrder: false,
          isMulti: false,
        });

  const styles = (() => {
    switch (size) {
      case 'md':
        return defaultReactSelectStyles({
          customStyles,
          hasError: hasError,
          portal: portal,
          theme: theme,
          autoExpand: autoExpand,
        });
      case 'sm':
        return defaultSmStyles(customStyles, hasError, portal, theme, autoExpand);
    }
  })();
  return (
    <CreatableSelect
      // render dropdown in a portal for proper behavior in modals, etc.
      menuPortalTarget={portal ? menuPortalTarget : undefined}
      menuPlacement='auto'
      {...passthroughProps}
      isDisabled={passthroughProps.disabled}
      isOptionDisabled={isOptionDisabled}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange={handleChange}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={transformedValue}
      options={options}
      isMulti={isMulti}
      isSearchable={isSearchable}
      placeholder={placeholder}
      className={className}
      styles={styles}
      components={components}
      onFocusOption={onFocusOption}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ref={inputRef}
    />
  );
};

// To be used in multi selects when an empty value represents "all selected".
// CSS ripped off of react-select to match MultiValue components
export const MultiValuePlaceholder = styled('div')`
  grid-area: 1 / 1 / 2 / 3;
  max-width: 100%;
  padding: 3px 14px;
  box-sizing: border-box;
  font-size: 85%;
  background-color: ${({ theme }) => theme.color.background.highlight};
  color: ${({ theme }) => theme.color.primary.brand};
  border-radius: 100px;
  font-weight: ${FontWeight.Medium};
`;

const ColoredSingleValue = styled(MultiValuePlaceholder)<{
  color: OptionColor;
}>`
  background-color: ${({ color }) => backgroundColorMap[color]};
  color: ${({ theme }) => theme.color.text.primary};
  transition: background-color ${TransitionDuration.Fast};
`;

const backgroundColorMap: Record<OptionColor, Color> = {
  purple: Color.CorePurple100,
  red: Color.CoreRed100,
  yellow: Color.CoreYellow100,
  green: Color.CoreGreen100,
  blue: Color.CoreBlue100,
  teal: Color.CoreTeal100,
  slate: Color.CoreSlate50,
};
